import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicManager } from 'managers/topic';
import { Tag } from 'models/tag';
import { TopicThread } from 'models/topicThread';
import { CommentsService } from 'services/comments';
import './ideationApprovalControls.html';
import './ideationApprovalControls.scss';

components.preload('tag');

export interface CommentIdeationApprovalControlsVMConfig {
    comment: SubscribableOrNullableValue<TopicThread>
}

@injectable()
export default class CommentIdeationApprovalControlsVM {
    private commentId: Subscribable<string | undefined>

    approvalTag: Tag

    isApproved: Subscribable<boolean>
    isReady: Subscribable<boolean>
    isBusy = ko.observable(false).extend({ deferred: true });

    constructor(
        @inject(wccModules.componentConfig) config: CommentIdeationApprovalControlsVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.commentsService) private commentsService: CommentsService
    ) {
        const comment = ko.flattenComputed(config.comment);
        const topicId = comment.pluck(c => c.topicId);

        this.commentId = comment.pluck(c => c.id);
        this.isApproved = comment.pluck(c => c.isApproved, false);

        this.approvalTag = new Tag({
            TagValue: labels.IdeationApprovedTag,
            TagColor: "#000000"
        });

        const userManager = storage.get(CurrentUserManager, { discussionId: settings.discussionId });
        const user = userManager.pluck('person');
        const isModerator = user.pluck(u => u.isModerator, false);

        const topicManager = storage.get(TopicManager, { topicId });
        const topic = topicManager.pluck('topic');
        const isIdeation = topic.pluck(t => t.isIdeation, false);
        const ideationStage = topic.pluck(t => t.ideationStage, 0);

        this.isReady = ko.pureComputed(() => isIdeation() && ideationStage() == enums.IdeationStages.IdeaReview.value && isModerator());

        ko.applyBindingsToNode(info.element, {
            'visible': this.isReady,

            'css': {
                'topic__comment__ideation-approval-controls topic__comment__section': true
            }
        });
    }

    toggleApproval = async () => {
        const commentId = this.commentId();

        if (commentId == undefined)
            throw new Error(messages.UnknownError);

        this.isBusy(true);

        try {
            if (this.isApproved())
                await this.commentsService.unapproveThread(commentId);
            else
                await this.commentsService.approveThread(commentId);
        } catch (ex) {
            system.handleError(ex);
        }finally {
            this.isBusy(false);
        }
    }
}